import { LitElement, html, css, classMap } from 'lit-all'
import { externalVideoEmbedSrc } from 'lib-utils'
import * as paths from 'lib-paths'

class NovaAdaptiveVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    id: {
      type: String,
    },
    title: {
      type: String,
    },
    autoplay: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    sound: {
      type: Boolean,
    },
    autoSound: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    host: {
      type: String,
    },
    description: {
      type: String,
    },
    aspectRatio: {
      type: String,
      attribute: 'aspect-ratio',
    },
    posterImage: {
      type: String,
      attribute: 'poster-image',
    },
    soundWithoutButton: {
      type: Boolean,
    },
    centerButtons: {
      type: Boolean,
    },
    leftControls: {
      type: Boolean,
    },
  }

  get computedAspectRatio() {
    return (
      this.videoObject?.aspect_ratio ||
      (this.aspectRatio === 'wide' && 16 / 9) ||
      (this.aspectRatio === 'full' && 4 / 3) ||
      eval(this.aspectRatio) ||
      16 / 9
    )
  }

  get videoElement() {
    return this.renderRoot?.getElementById('video-element') ?? null
  }

  connectedCallback() {
    super.connectedCallback()
    this.playing = this.autoplay || false
    this.style.setProperty('--nova-aspect-ratio', this.computedAspectRatio)
  }

  firstUpdated() {
    // For Shopify uploaded video only
    if (this.controls) {
      this.handleControlBarEvents()
    }

    // always start muted apart from video without sound button
    if (!this.soundWithoutButton) {
      this.muteVideo()
      this.muted = true
    }

    if (this.autoSound) {
      this.muted = true
      this.muteVideo()
    }
  }

  handleControlBarEvents() {
    const video = this.renderRoot.querySelector('#video-element')
    if (!video) return
    const buttonContainer = this.renderRoot.querySelector(
      '.js-button-with-control-bar'
    )
    const button = buttonContainer.querySelector('.js-video-control-button')
    const playIcon = button.querySelector('#play')
    const pauseIcon = button.querySelector('#pause')

    video.addEventListener('play', () => {
      this.playing = true
      pauseIcon.classList.remove('hidden')
      playIcon.classList.add('hidden')
      buttonContainer.classList.add('hidden')
    })

    video.addEventListener('pause', () => {
      this.playing = false
      pauseIcon.classList.add('hidden')
      playIcon.classList.remove('hidden')
      buttonContainer.classList.remove('hidden')
    })
  }

  playVideo(playIcon, pauseIcon) {
    this.playing = true
    const overlay = this.renderRoot?.querySelector('.video-overlay')
    if (overlay) overlay.style.display = 'none'

    this.videoElement.tagName == 'VIDEO'
      ? this.videoElement.play()
      : this.videoElement.tagName == 'IFRAME'
      ? (this.videoElement.src = this.videoElement.src.replace(
          'autoplay=0',
          'autoplay=1'
        ))
      : null

    pauseIcon.classList.remove('hidden')
    playIcon.classList.add('hidden')
  }

  pauseVideo(playIcon, pauseIcon) {
    this.playing = false

    this.videoElement.tagName == 'VIDEO'
      ? this.videoElement.pause()
      : this.videoElement.tagName == 'IFRAME'
      ? (this.videoElement.src = this.videoElement.src.replace(
          'autoplay=1',
          'autoplay=0'
        ))
      : null

    pauseIcon.classList.add('hidden')
    playIcon.classList.remove('hidden')
  }

  togglePlayPause(e) {
    const button = e.target.closest('.js-video-control-button')
    const playIcon = button.querySelector('#play')
    const pauseIcon = button.querySelector('#pause')

    this.playing
      ? this.pauseVideo(playIcon, pauseIcon)
      : this.playVideo(playIcon, pauseIcon)
  }

  muteVideo() {
    const video = this.renderRoot.querySelector('#video-element')
    const muteIcon = this.renderRoot.querySelector('#mute')
    const unmuteIcon = this.renderRoot.querySelector('#unmute')

    if (this.muted) {
      video.muted = false
      this.muted = false
      muteIcon ? muteIcon.classList.add('hidden') : null
      unmuteIcon ? unmuteIcon.classList.remove('hidden') : null
    } else {
      video.muted = true
      this.muted = true
      muteIcon ? muteIcon.classList.remove('hidden') : null
      unmuteIcon ? unmuteIcon.classList.add('hidden') : null
    }
  }

  renderPosterImage() {
    if (!this.posterImage) return html``
    return html`
      <div class="video-overlay">
        <img src="${this.posterImage}" />
      </div>
    `
  }

  renderVideoControls() {
    return html`
    <div class="buttons-container ${classMap({
      'buttons-layout-center': this.centerButtons,
      'js-button-with-control-bar': this.controls,
      'buttons-container--left': this.leftControls,
    })}">
        <button
          class="js-video-control-button video-pause-button"
          @click="${this.togglePlayPause}"
        >
          ${this.autoplay
            ? html`<span id="play" class="hidden">${paths.play}</span>
                <span id="pause">${paths.pause}</span>`
            : html`<span id="play">${paths.play}</span>
                <span id="pause" class="hidden">${paths.pause}</span>`}
        </button>
        ${this.sound
          ? html`
              <button
                class="js-video-control-button video-mute-button"
                @click="${this.muteVideo}"
              >
                <span id="mute">${paths.mute}</span>
                <span id="unmute" class="hidden">${paths.muted}</span>
              </button>
            `
          : ``}
      </div>
    `
  }

  renderVideo() {
    return html`
      <video
        id="video-element"
        playsinline
        ?autoplay="${this.autoplay}"
        ?controls="${this.controls}"
        ?loop="${this.loop}"
        ${this.posterImage ? `poster="${this.posterImage}"` : html``}
        preload="none"
        aria-label="${this.description}"
      >
        ${this.videoObject.sources.map(
          (source) =>
            html`<source src="${source.url}" type="${source.mime_type}"></source>`
        )}
      </video>
    `
  }

  renderIframe() {
    return html`
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        loading="lazy"
        src="${externalVideoEmbedSrc(
          this.host,
          this.id,
          this.autoplay,
          this.controls,
          this.loop,
          this.sound
        )}"
        title="${this.title}"
      >
      </iframe>
    `
  }

  render() {
    return html`
      ${this.title ? html`<h2>${this.title}</h2>` : html``}
      ${['vimeo', 'youtube'].includes(this.host)
        ? this.renderIframe()
        : this.renderVideo()}
      ${this.autoplay ? html`` : this.renderPosterImage()}
      ${this.renderVideoControls()}
    `
  }

  static styles = css`
    :host {
      aspect-ratio: var(--nova-aspect-ratio, 16 / 9);
      display: block;
      position: relative;
    }

    #video-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      border-radius: var(--nova-border-radius-l);
    }

    .video-overlay {
      position: absolute;
      display: flex;
      top: -1px;
      bottom: -1px;
      left: -1px;
      right: -1px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .video-overlay-text {
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    @media screen and (max-width: 749px) {
      .video-overlay-text.hidden--sm {
        display: none;
      }
    }

    h2,
    p {
      color: var(--video-overlay-text-color);
    }

    h2 {
      margin: 0;
      font-size: 48px;
      font-weight: 300;
      line-height: 110%;
      letter-spacing: -2px;
    }

    p {
      width: 50%;
      text-align: center;
      margin-top: 10px;
      margin-bottom: 30px;
    }

    .video-overlay img {
      // position: absolute;
      // top: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      border-radius: var(--nova-border-radius-l);
    }

    .buttons-container {
      position: absolute;
      z-index: 1;
      bottom: 0;
      right: 0;
      margin: var(--nova-space-xs);
      display: flex;
      gap: var(--nova-space-2xs);
    }

    .buttons-container--left {
      left: 0;
    }

    .buttons-layout-center.buttons-container {
      top: 50%;
      right: 50%;
      transform: translate(calc(50% + 8px), calc((50% + 8px) * -1));
    }

    .js-video-control-button {
      align-items: center;
      background: var(--nova-brand-primary);
      border: none;
      border-radius: var(--nova-border-radius-m);
      cursor: pointer;
      width: 46px;
      height: 46px;
      display: flex;
      justify-content: center;
    }

    .js-video-control-button:hover {
      background: var(--nova-color-lilac);
    }

    .js-video-control-button svg,
    .js-video-control-button span {
      position: relative;
      width: 30px;
      height: 30px;
    }

    .hidden {
      display: none;
    }
  `
}

window.customElements.define('nova-adaptive-video', NovaAdaptiveVideo)
